import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Add, Cancel, CheckCircle, Edit, Search, TwoWheeler } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import type { Entregador, SalvarEntregadorPayload } from "../../../services/entregadores"
import { salvarEntregador } from "../../../services/entregadores"
import { EntregadoresActionsTypes } from "../context/entregadoresActions"
import { useEntregadoresContext } from "../context/useEntregadoresContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: theme.spacing(3),
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
    },
    addButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        boxShadow: "0 2px 8px rgba(76,175,80,0.4)",
        "&:hover": { backgroundColor: "#43a047" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    emptyText: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(3),
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        boxShadow: "0 2px 8px rgba(76,175,80,0.4)",
        "&:hover": { backgroundColor: "#43a047" },
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
    ativoIcon: {
        color: "#0fbb3c",
    },
    inativoIcon: {
        color: "#e74a3b",
    },
}))

const formularioVazio: SalvarEntregadorPayload = {
    id: null,
    nome: "",
    telefone: "",
    placaVeiculo: "",
    ativo: true,
}

const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()

export function EntregadoresForm() {
    const classes = useStyles()
    const { dados, dispatch, erroCarregamento } = useEntregadoresContext()
    const { enqueueSnackbar } = useSnackbar()

    const [termoBusca, setTermoBusca] = useState("")
    const [mostrarInativos, setMostrarInativos] = useState(false)
    const [dialogAberto, setDialogAberto] = useState(false)
    const [formulario, setFormulario] = useState(formularioVazio)
    const [salvando, setSalvando] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const listaFiltrada = dados.lista.filter(e => {
        const matchAtivo = mostrarInativos ? true : e.ativo
        const matchNome = termoBusca ? normalizar(e.nome).includes(normalizar(termoBusca)) : true
        return matchAtivo && matchNome
    })

    const abrirNovoEntregador = () => {
        setFormulario(formularioVazio)
        setDialogAberto(true)
    }

    const abrirEditarEntregador = (e: Entregador) => {
        setFormulario({
            id: e.id,
            nome: e.nome,
            telefone: e.telefone,
            placaVeiculo: e.placaVeiculo ?? "",
            ativo: e.ativo,
        })
        setDialogAberto(true)
    }

    const handleSalvar = async () => {
        if (!formulario.nome.trim()) {
            alertStart("O campo Nome é obrigatório.", "error")
            return
        }
        if (!formulario.telefone.trim()) {
            alertStart("O campo Telefone é obrigatório.", "error")
            return
        }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setSalvando(true)
        try {
            const response = await salvarEntregador(autorizacao, formulario)
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao salvar entregador", "error")
                return
            }
            const novaLista = formulario.id
                ? dados.lista.map(e => (e.id === response.id ? response : e))
                : [...dados.lista, response]
            dispatch({ type: EntregadoresActionsTypes.CARREGAR, payload: novaLista })
            setDialogAberto(false)
            alertStart("Entregador salvo com sucesso!", "success")
        } finally {
            setSalvando(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box className={classes.titleRow}>
                    <Typography variant="h5" className={classes.titleText}>
                        Entregadores
                    </Typography>
                    <Button
                        className={classes.addButton}
                        variant="contained"
                        startIcon={<Add />}
                        onClick={abrirNovoEntregador}
                    >
                        + Entregador(a)
                    </Button>
                </Box>

                {/* Filtros */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Search className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtros
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Buscar por nome"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={termoBusca}
                                    onChange={e => setTermoBusca(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search
                                                    style={{ color: "#7f8c8d", fontSize: 20 }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mostrarInativos}
                                            onChange={e => setMostrarInativos(e.target.checked)}
                                            color="default"
                                        />
                                    }
                                    label="Mostrar inativos"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Lista */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <TwoWheeler className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Entregadores cadastrados ({listaFiltrada.length})
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {listaFiltrada.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                Nenhum entregador encontrado para este filtro.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table size="small" style={{ width: "100%", minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 160 }}>Nome</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Telefone</TableCell>
                                            <TableCell style={{ minWidth: 90 }}>Placa</TableCell>
                                            <TableCell style={{ minWidth: 160 }}>
                                                Estabelecimento
                                            </TableCell>
                                            <TableCell style={{ minWidth: 60 }} align="center">
                                                Ativo
                                            </TableCell>
                                            <TableCell style={{ minWidth: 60 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {listaFiltrada.map(entregador => (
                                            <TableRow key={entregador.id}>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={entregador.nome}
                                                >
                                                    {entregador.nome}
                                                </TableCell>
                                                <TableCell>{entregador.telefone}</TableCell>
                                                <TableCell>{entregador.placaVeiculo}</TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={
                                                        entregador.estabelecimentoNome ?? undefined
                                                    }
                                                >
                                                    {entregador.estabelecimentoNome}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {entregador.ativo ? (
                                                        <CheckCircle
                                                            className={classes.ativoIcon}
                                                            fontSize="small"
                                                            titleAccess="Ativo"
                                                        />
                                                    ) : (
                                                        <Cancel
                                                            className={classes.inativoIcon}
                                                            fontSize="small"
                                                            titleAccess="Inativo"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        title="Editar"
                                                        onClick={() =>
                                                            abrirEditarEntregador(entregador)
                                                        }
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Dialog criar/editar */}
            <Dialog
                open={dialogAberto}
                onClose={() => setDialogAberto(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {formulario.id ? "Editar Entregador(a)" : "Cadastrar Entregador(a)"}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} style={{ marginTop: 4 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Nome"
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                value={formulario.nome}
                                onChange={e => {
                                    const value = e.target.value
                                    setFormulario(prev => ({ ...prev, nome: value }))
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Telefone"
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                placeholder="DDD+número"
                                value={formulario.telefone}
                                onChange={e => {
                                    const value = e.target.value
                                    setFormulario(prev => ({ ...prev, telefone: value }))
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Placa do veículo"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={formulario.placaVeiculo}
                                onChange={e => {
                                    const value = e.target.value
                                    setFormulario(prev => ({ ...prev, placaVeiculo: value }))
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formulario.ativo}
                                        onChange={e => {
                                            const checked = e.target.checked
                                            setFormulario(prev => ({ ...prev, ativo: checked }))
                                        }}
                                        color="default"
                                    />
                                }
                                label="Ativo"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogAberto(false)} disabled={salvando}>
                        Cancelar
                    </Button>
                    <Button
                        className={classes.saveButton}
                        variant="contained"
                        onClick={handleSalvar}
                        disabled={salvando}
                    >
                        {salvando ? "Salvando..." : "Salvar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
